import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Phone } from "lucide-react";

// Configure once — everything else derives from these.
const WHATSAPP_NUMBER = "919382166788"; // No +, no spaces — required by wa.me
const DEFAULT_MESSAGE =
  "Hi Language Nest India 👋 I have a question about your courses / study abroad services. Could you help me?";
const HIDDEN_ROUTES = ["/admin", "/survey", "/auth/callback"];

const QUICK_OPTIONS = [
  { label: "IELTS coaching",              msg: "Hi! I want to know about the IELTS coaching batches at Language Nest India." },
  { label: "PTE / Spoken English",        msg: "Hi! I'd like details on your PTE and Spoken English batches." },
  { label: "Study Abroad consultation",   msg: "Hi! I'm interested in study abroad guidance. Could we schedule a free consultation?" },
  { label: "Book a free demo",            msg: "Hi! I'd like to book a free demo class. Please share the available slots." },
];

const openWhatsApp = (message) => {
  const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message || DEFAULT_MESSAGE)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

export const WhatsappFloatingButton = () => {
  const [open, setOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const { pathname } = useLocation();

  // Auto-invite bubble ~4s after landing (only once per session)
  useEffect(() => {
    if (sessionStorage.getItem("lni_wa_invite_seen")) return;
    const t = setTimeout(() => {
      setShowBubble(true);
      sessionStorage.setItem("lni_wa_invite_seen", "1");
      setTimeout(() => setShowBubble(false), 6000);
    }, 4500);
    return () => clearTimeout(t);
  }, []);

  if (HIDDEN_ROUTES.some((p) => pathname.startsWith(p))) return null;

  return (
    <div className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-[60] flex flex-col items-end gap-3" data-testid="whatsapp-floating-widget">
      {/* Quick chat card */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="wa-card"
            initial={{ opacity: 0, y: 16, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.94 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="w-[92vw] sm:w-80 rounded-3xl bg-white overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)] border border-brand-navy/10"
            data-testid="whatsapp-chat-card"
          >
            {/* Card header */}
            <div className="relative px-5 py-4 bg-[#075E54] text-white overflow-hidden">
              <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white/10" />
              <div className="relative flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white grid place-items-center shrink-0">
                    <span className="text-xl">🏫</span>
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-sm truncate">Language Nest India</div>
                    <div className="flex items-center gap-1.5 text-[11px] text-white/80">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                      Typically replies in 5 min
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="whatsapp-close-btn"
                  aria-label="Close chat"
                  className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 grid place-items-center transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Card body — chat-style bubble */}
            <div
              className="relative px-5 py-6 space-y-3"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'><circle cx='30' cy='30' r='1.5' fill='%2314213D' opacity='0.05'/></svg>\")",
                backgroundColor: "#ECE5DD",
              }}
            >
              <div className="relative max-w-[85%] rounded-2xl rounded-tl-none bg-white px-4 py-3 shadow-sm">
                <div className="text-[13px] text-brand-navy leading-relaxed">
                  👋 Hi there! How can we help you today? Pick a topic — we&#39;ll continue on WhatsApp.
                </div>
                <div className="mt-1 text-[10px] text-brand-navy/40 text-right">Just now</div>
              </div>
            </div>

            {/* Quick actions */}
            <div className="p-4 space-y-2 bg-white border-t border-brand-navy/5">
              {QUICK_OPTIONS.map((o) => (
                <button
                  key={o.label}
                  onClick={() => { setOpen(false); openWhatsApp(o.msg); }}
                  data-testid={`whatsapp-quick-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="w-full text-left px-4 py-2.5 rounded-full bg-brand-muted-bg hover:bg-brand-red hover:text-white text-sm font-semibold text-brand-navy transition-colors border border-brand-navy/5"
                >
                  {o.label}
                </button>
              ))}

              <div className="pt-2 mt-2 border-t border-brand-navy/5 flex items-center justify-between text-[11px] text-brand-navy/50">
                <a href="tel:+919382166788" data-testid="whatsapp-call-fallback" className="flex items-center gap-1.5 hover:text-brand-red transition-colors">
                  <Phone className="w-3 h-3" /> Or call +91 93821 66788
                </a>
                <span>Powered by WhatsApp</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Auto-invite bubble */}
      <AnimatePresence>
        {!open && showBubble && (
          <motion.button
            key="wa-bubble"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            onClick={() => { setShowBubble(false); setOpen(true); }}
            data-testid="whatsapp-invite-bubble"
            className="relative bg-white rounded-2xl rounded-br-none shadow-lg border border-brand-navy/10 px-4 py-3 text-left max-w-[240px] pr-9"
          >
            <div className="text-xs font-semibold text-brand-navy">
              Have a question? 💬<br />
              <span className="text-brand-navy/60 font-medium">Chat with us on WhatsApp — we usually reply in 5 mins.</span>
            </div>
            <span
              onClick={(e) => { e.stopPropagation(); setShowBubble(false); }}
              className="absolute top-2 right-2 w-5 h-5 rounded-full bg-brand-muted-bg text-brand-navy/60 grid place-items-center hover:bg-brand-red hover:text-white transition"
            >
              <X className="w-3 h-3" />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Main FAB */}
      <motion.button
        onClick={() => { setShowBubble(false); setOpen((v) => !v); }}
        data-testid="whatsapp-fab"
        aria-label={open ? "Close WhatsApp chat" : "Chat with us on WhatsApp"}
        initial={{ scale: 0, rotate: -90 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.4 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full grid place-items-center text-white shadow-[0_12px_30px_-8px_rgba(37,211,102,0.6)]"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, #43e97b, #25D366 55%, #128C7E 100%)",
        }}
      >
        {/* Pulse ring */}
        {!open && (
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-40 pointer-events-none" />
        )}
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span
              key="x"
              initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} />
            </motion.span>
          ) : (
            <motion.span
              key="wa"
              initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* WhatsApp SVG mark */}
              <svg viewBox="0 0 24 24" className="w-7 h-7 sm:w-8 sm:h-8" fill="currentColor" aria-hidden="true">
                <path d="M20.52 3.48A11.86 11.86 0 0 0 12 0C5.37 0 0 5.37 0 12a11.86 11.86 0 0 0 1.64 6.02L0 24l6.13-1.61A11.87 11.87 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.19-1.24-6.19-3.48-8.52ZM12 22a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.64.95.97-3.55-.23-.36A9.87 9.87 0 0 1 2.14 12c0-5.44 4.42-9.86 9.86-9.86A9.83 9.83 0 0 1 21.86 12c0 5.44-4.42 10-9.86 10Zm5.44-7.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.08-.3-.15-1.28-.47-2.44-1.5-.9-.8-1.5-1.78-1.68-2.08-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.08-.79.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.9 1.22 3.1.15.2 2.1 3.2 5.09 4.49.71.3 1.26.48 1.69.62.71.23 1.36.2 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35Z"/>
              </svg>
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
};
